import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { RevenueRange, useRevenue } from '../../queries/revenue';
import { PageHeader } from '../../components/ui/PageHeader';
import { StatCard } from '../../components/ui/StatCard';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';

const RANGES: { value: RevenueRange; label: string }[] = [
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
];

const money = (cents: number) => `$${(cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const DATE = new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });

interface TopCustomer {
  userId: number;
  displayName: string;
  invoiceCount: number;
  lastPaymentDate: string | null;
  totalPaidCents: number;
}

export function RevenuePage() {
  const [range, setRange] = useState<RevenueRange>('12m');
  const { data, isLoading } = useRevenue(range);

  const customerColumns: DataTableColumn<TopCustomer>[] = [
    { key: 'name', header: 'Top customers', priority: true, render: (c) => <span className="font-medium">{c.displayName}</span> },
    { key: 'invoices', header: 'Invoices', priority: false, render: (c) => <span className="tabular-nums text-muted-foreground @2xl:text-foreground">{c.invoiceCount}</span> },
    { key: 'last', header: 'Last payment', priority: false, render: (c) => <span className="text-muted-foreground @2xl:text-foreground">{c.lastPaymentDate ? DATE.format(new Date(c.lastPaymentDate)) : '—'}</span> },
    { key: 'total', header: 'Total paid', priority: true, alignEnd: true, render: (c) => <span className="font-medium tabular-nums">{money(c.totalPaidCents)}</span> },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Revenue"
        description="Stripe billing. Stat cards and trends are all-time; the range filters the lower cards."
        actions={
          <select
            value={range}
            onChange={(e) => setRange(e.target.value as RevenueRange)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {RANGES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        }
      />

      {isLoading || !data ? (
        <Spinner />
      ) : (
        <>
          {/* All-time stat cards. */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <StatCard label="Total Revenue" value={money(data.stats.totalRevenueCents)} />
            <StatCard label="This Month" value={money(data.stats.thisMonthCents)} />
            <StatCard label="Last 7 Days" value={money(data.stats.last7dCents)} />
            <StatCard label="ARPU" value={money(data.stats.arpuCents)} />
            <StatCard label="Paying Users" value={data.stats.payingUserCount.toLocaleString()} />
          </div>

          {/* Fixed-window trend charts. */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <TrendChart title="Monthly revenue (last 12 months)" data={data.monthlyTrend} />
            <TrendChart title="Weekly revenue (last 13 weeks)" data={data.weeklyTrend} />
          </div>

          {/* Range-filtered cards. */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>Revenue by billing reason</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 pt-0">
                {data.breakdown.length === 0 && <p className="text-sm text-muted-foreground">No paid invoices in range.</p>}
                {data.breakdown.map((b) => (
                  <div key={b.billingReason} className="flex items-center justify-between text-sm">
                    <span>{b.billingReason} <span className="text-xs text-muted-foreground">({b.invoiceCount})</span></span>
                    <span className="font-medium tabular-nums">{money(b.revenueCents)}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Invoice health</CardTitle></CardHeader>
              <CardContent className="space-y-1.5 pt-0">
                {data.health.map((h) => (
                  <div key={h.status} className="flex items-center justify-between text-sm">
                    <span className="capitalize">{h.status} <span className="text-xs text-muted-foreground">({h.invoiceCount})</span></span>
                    <span className="font-medium tabular-nums">
                      {money(h.amountPaidCents)}
                      {h.amountDueCents > 0 && <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">/ {money(h.amountDueCents)} due</span>}
                    </span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <DataTable columns={customerColumns} data={data.topCustomers} getRowKey={(c) => String(c.userId)} emptyMessage="No paid invoices in range." />
        </>
      )}
    </div>
  );
}

function TrendChart({ title, data }: { title: string; data: { periodLabel: string; revenueCents: number }[] }) {
  return (
    <Card>
      <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <CardContent className="pt-0">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.map((d) => ({ ...d, revenue: d.revenueCents / 100 }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="periodLabel" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} interval="preserveStartEnd" stroke="hsl(var(--border))" />
              <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} stroke="hsl(var(--border))" />
              <Tooltip
                contentStyle={{ fontSize: 12, background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--popover-foreground))' }}
                cursor={{ fill: 'hsl(var(--muted) / 0.5)' }}
                formatter={(v: number) => [`$${v.toLocaleString('en-AU')}`, 'Revenue']}
              />
              <Bar dataKey="revenue" name="Revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
